/**
 * Runs before React renders. Handles everything that must reach the browser as
 * early as possible: SEO metadata, social cards, structured data, non-blocking
 * font loading and the LCP image preload.
 */
import { HERO_IMAGE } from '../data/content'
import { HERO_WIDTHS, IMAGE_PROXY_ORIGIN, optimizedImage, optimizedSrcSet, HERO_SIZES } from './images'

const TITLE = 'Surgical Shield System 2026 | Surgical Technology Faculty Resource Suite'
const DESCRIPTION =
  'Surgical Shield System 2026: 6 digital volumes plus 5 bonuses for Surgical Technology faculty — protocols, solved clinical cases, rubrics and semester planning. Instant access, one-time payment of $27, 7-day guarantee.'
const KEYWORDS =
  'surgical technology curriculum, surgical tech instructor resources, perioperative safety lesson plans, WHO surgical checklist teaching, surgical instrumentation course material'

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function upsertLink(rel: string, href: string, extra: Record<string, string> = {}): HTMLLinkElement {
  let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"][href="${href}"]`)
  if (!tag) {
    tag = document.createElement('link')
    tag.rel = rel
    tag.href = href
    document.head.appendChild(tag)
  }
  Object.entries(extra).forEach(([name, value]) => tag?.setAttribute(name, value))
  return tag
}

export function setupPage(): void {
  if (typeof document === 'undefined') return

  document.documentElement.lang = 'en'
  document.title = TITLE

  upsertMeta('name', 'description', DESCRIPTION)
  upsertMeta('name', 'keywords', KEYWORDS)
  upsertMeta('name', 'robots', 'index, follow, max-image-preview:large')
  upsertMeta('name', 'theme-color', '#112742')
  upsertMeta('name', 'author', 'Surgical Shield System')

  const url = window.location.origin + window.location.pathname
  upsertLink('canonical', url)

  upsertMeta('property', 'og:type', 'product')
  upsertMeta('property', 'og:site_name', 'Surgical Shield System')
  upsertMeta('property', 'og:title', TITLE)
  upsertMeta('property', 'og:description', DESCRIPTION)
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', HERO_IMAGE)
  upsertMeta('property', 'og:locale', 'en_US')
  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', TITLE)
  upsertMeta('name', 'twitter:description', DESCRIPTION)
  upsertMeta('name', 'twitter:image', HERO_IMAGE)

  // Structured data: product offer + FAQ, so search results can show price and questions.
  if (!document.getElementById('ss-structured-data')) {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.id = 'ss-structured-data'
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Surgical Shield System 2026',
      description: DESCRIPTION,
      image: HERO_IMAGE,
      brand: { '@type': 'Brand', name: 'Surgical Shield System' },
      offers: {
        '@type': 'Offer',
        price: '27.00',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: 'https://surgical-shield-system-2026.impultienda.ar/checkout',
      },
      aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '150' },
    })
    document.head.appendChild(script)
  }

  // LCP: preconnect to the image hosts and preload the hero at the exact widths used.
  upsertLink('preconnect', IMAGE_PROXY_ORIGIN, { crossorigin: '' })
  upsertLink('preconnect', 'https://cdn.magicpatterns.com', { crossorigin: '' })
  const preload = document.createElement('link')
  preload.rel = 'preload'
  preload.as = 'image'
  preload.href = optimizedImage(HERO_IMAGE, 540)
  preload.setAttribute('imagesrcset', optimizedSrcSet(HERO_IMAGE, HERO_WIDTHS))
  preload.setAttribute('imagesizes', HERO_SIZES)
  preload.setAttribute('fetchpriority', 'high')
  document.head.appendChild(preload)

  // Webfonts are entirely off the critical path: the page paints with system
  // fonts, and Oswald/Inter are requested once the browser is idle.
  const loadFonts = () => {
    upsertLink('preconnect', 'https://fonts.gstatic.com', { crossorigin: '' })
    const fonts = document.createElement('link')
    fonts.rel = 'stylesheet'
    fonts.href =
      'https://fonts.googleapis.com/css2?family=Oswald:wght@600;700&family=Inter:wght@400;600&display=swap'
    document.head.appendChild(fonts)
  }

  const schedule = () => {
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
      .requestIdleCallback
    if (idle) {
      idle(loadFonts)
    } else {
      window.setTimeout(loadFonts, 1200)
    }
  }

  if (document.readyState === 'complete') {
    schedule()
  } else {
    window.addEventListener('load', schedule, { once: true })
  }
}
